/**
 * command-items.ts — Danh sách lệnh của bảng ⌘K (command-palette.tsx), dựng từ ĐÚNG các nguồn của menu:
 * menu chính (nav-config, đã lọc theo vai trò), mục Cài đặt hệ thống (settings-modules), Cài đặt cá nhân
 * (account-modules) và vài thao tác nhanh. Nguyên tắc BRD §5.3.1 như menu: không có quyền → không hiện.
 * Thêm trang mới vào menu là tự có trong bảng lệnh — chỉ thao tác nhanh mới khai báo tay ở đây.
 */
import {
  Bot, Gem, MessageSquarePlus, Plug, Rocket, Settings, UserCog, type LucideIcon,
} from 'lucide-react'
import type { Role } from '@/types/api'
import type { ChannelKey } from '@/pages/integrations/channel-catalog'
import { i18n } from '@/lib/i18n'
import { settingsGroupsForRole } from '@/pages/settings/settings-modules'
import { ACCOUNT_MODULES_META } from '@/pages/account/account-modules'
import { navForRole } from './nav-config'
import { CUSTOMER_TABS } from '@/pages/customers/customers-tabs'

export type CommandSection = 'goto' | 'settings' | 'quickActions'

export function commandSectionLabel(section: CommandSection): string {
  return i18n.t(`sharedMisc:commandItems.section.${section}`)
}

export interface CommandItem {
  /** Duy nhất trong danh sách — dùng làm key / id phần tử. */
  id: string
  label: string
  /** Đường dẫn cha hiện mờ bên phải, vd "Đội ngũ › Nhân viên AI". */
  group: string
  section: CommandSection
  icon: LucideIcon
  /** Trang đích. */
  to?: string
  /** Lệnh không phải điều hướng. */
  run?: 'feedback'
  /** Chỉ để tìm (mô tả, từ đồng nghĩa) — không hiển thị. */
  keywords?: string[]
}

/** Sự kiện cửa sổ để mở hộp "Gửi Feedback" (nút ở đáy menu trái lắng nghe — side-nav.tsx). */
export const OPEN_FEEDBACK_EVENT = 'ecdp:open-feedback'

/** Kênh hay kết nối nhất → thao tác "Kết nối …" (trang /integrations/<key>). */
function connectChannels(): { key: ChannelKey; name: string }[] {
  return [
    { key: 'zalo-personal', name: i18n.t('sharedMisc:commandItems.connectChannels.zaloPersonal') },
    { key: 'zalo-oa', name: i18n.t('sharedMisc:commandItems.connectChannels.zaloOa') },
    { key: 'zalo-bot', name: i18n.t('sharedMisc:commandItems.connectChannels.zaloBot') },
    { key: 'facebook', name: i18n.t('sharedMisc:commandItems.connectChannels.facebook') },
    { key: 'telegram-bot', name: i18n.t('sharedMisc:commandItems.connectChannels.telegramBot') },
    { key: 'telegram-app', name: i18n.t('sharedMisc:commandItems.connectChannels.telegramPersonal') },
    { key: 'website', name: i18n.t('sharedMisc:commandItems.connectChannels.website') },
  ]
}

export function buildCommandItems(role?: Role, perms?: Set<string>): CommandItem[] {
  if (!role) return []
  const nav = navForRole(role, perms)
  const pages: CommandItem[] = []
  // Tên cũ vẫn tìm ra: "Hộp thư đến" đã đổi thành "Nhắn tin" (nav-config) — ai quen gõ tên cũ không bị hụt.
  const OLD_NAMES: Record<string, string[]> = { '/conversations': ['hộp thư', 'hộp thư đến', 'inbox', 'chat'] } // i18n-ignore — từ khoá tìm ⌘K, không phải chữ hiển thị
  const page = (label: string, group: string, to: string, icon: LucideIcon) =>
    pages.push({ id: `page:${to}`, label, group, section: 'goto', icon, to, keywords: OLD_NAMES[to] })

  for (const s of nav) {
    if (!s.children) {
      page(s.label, '', s.to, s.icon)
      continue
    }
    for (const c of s.children) {
      page(c.label, s.label, c.to, s.icon)
      for (const g of c.children ?? []) page(g.label, `${s.label} › ${c.label}`, g.to, s.icon)
    }
  }
  // Module Khách hàng là MỘT mục menu, các trang con là tab (customers-tabs.tsx) → đưa từng tab vào bảng lệnh để tìm được
  // "Trả lời nhanh", "Khách trùng lặp"… (quyền trang con vẫn do trang / máy chủ kiểm).
  const customers = nav.find((s) => s.key === 'customers')
  if (customers) {
    for (const t of CUSTOMER_TABS) {
      if (t.to === customers.to || (t.roles && !t.roles.includes(role))) continue
      page(t.label, customers.label, t.to, customers.icon)
    }
  }
  page(i18n.t('sharedMisc:commandItems.billing'), i18n.t('sharedMisc:commandItems.account'), '/billing', Gem)
  page(i18n.t('sharedMisc:commandItems.onboarding'), i18n.t('sharedMisc:commandItems.account'), '/onboarding', Rocket)

  const has = (key: string) => nav.some((s) => s.key === key)
  const settings: CommandItem[] = []
  if (has('settings')) {
    for (const g of settingsGroupsForRole(role, perms)) {
      for (const m of g.items) {
        settings.push({
          id: `settings:${m.id}`, label: m.label, group: i18n.t('sharedMisc:commandItems.settingsGroup', { title: g.title }), section: 'settings',
          icon: Settings, to: `/settings?m=${m.id}`, keywords: [m.description, ...(m.keywords ?? [])],
        })
      }
    }
  }
  for (const m of ACCOUNT_MODULES_META) {
    settings.push({
      id: `account:${m.id}`, label: m.label, group: i18n.t('sharedMisc:commandItems.personalSettings'), section: 'settings',
      icon: UserCog, to: `/account/${m.id}`, keywords: [m.description],
    })
  }

  const actions: CommandItem[] = []
  if (has('connect')) {
    for (const c of connectChannels()) {
      actions.push({
        id: `connect:${c.key}`, label: i18n.t('sharedMisc:commandItems.connectChannelLabel', { name: c.name }), group: i18n.t('sharedMisc:commandItems.connectChannelsGroup'), section: 'quickActions',
        icon: Plug, to: `/integrations/${c.key}`, keywords: ['thêm kênh', 'tích hợp'], // i18n-ignore — từ khoá tìm ⌘K
      })
    }
  }
  if (pages.some((p) => p.to === '/ai/nhan-vien')) {
    actions.push({
      id: 'action:new-bot', label: i18n.t('sharedMisc:commandItems.newBot'), group: i18n.t('sharedMisc:commandItems.newBotGroup'), section: 'quickActions',
      icon: Bot, to: '/ai/nhan-vien/moi', keywords: ['thêm bot', 'chatbot'], // i18n-ignore — từ khoá tìm ⌘K
    })
  }
  actions.push({
    id: 'action:feedback', label: i18n.t('sharedMisc:commandItems.sendFeedback'), group: i18n.t('sharedMisc:commandItems.feedbackGroup'), section: 'quickActions',
    icon: MessageSquarePlus, run: 'feedback', keywords: ['báo lỗi', 'góp ý', 'hỗ trợ'], // i18n-ignore — từ khoá tìm ⌘K
  })

  return [...pages, ...settings, ...actions]
}
